import { useState, useEffect } from 'react'
import { getUserTimezone } from '@/lib/timezone'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Globe,
  Link2,
  MessageCircle,
  Trophy,
  ExternalLink
} from 'lucide-react'
import { Link } from 'react-router-dom'
import api from '@/lib/api'

interface PageData {
  path: string
  title: string
  chatCount: number
  uniqueVisitors: number
}

interface UtmData {
  source: string
  medium: string
  campaign: string
  count: number
  conversions: number
}

interface QuestionData {
  question: string
  count: number
  category?: string
}

interface AgentData {
  id: string
  name: string
  chatsHandled: number
  avgResponseTime: number
  satisfaction?: number
}

interface AnalyticsTabsProps {
  timeframe?: string
}

export default function AnalyticsTabs({ timeframe = '7d' }: AnalyticsTabsProps) {
  const [activeTab, setActiveTab] = useState('questions')
  const [topPages, setTopPages] = useState<PageData[]>([])
  const [utmSources, setUtmSources] = useState<UtmData[]>([])
  const [questions, setQuestions] = useState<QuestionData[]>([])
  const [agents, setAgents] = useState<AgentData[]>([])
  const [loading, setLoading] = useState<Record<string, boolean>>({
    pages: true,
    utm: true,
    questions: true,
    agents: true
  })

  // Load the active tab's data on mount, tab change, or timeframe change
  useEffect(() => {
    const loadActiveTab = () => {
      if (activeTab === 'pages') loadTopPages()
      else if (activeTab === 'utm') loadUtmSources()
      else if (activeTab === 'questions') loadQuestions()
      else if (activeTab === 'agents') loadAgents()
    }
    loadActiveTab()
  }, [activeTab, timeframe])

  const loadTopPages = async () => {
    setLoading(prev => ({ ...prev, pages: true }))
    try {
      const tz = encodeURIComponent(getUserTimezone())
      const response = await api.get(`/api/analytics/top-pages?timeframe=${timeframe}&limit=5&timezone=${tz}`)
      if (response.data.success) {
        setTopPages(response.data.data || [])
      }
    } catch (error) {
      console.error('Failed to load top pages:', error)
    } finally {
      setLoading(prev => ({ ...prev, pages: false }))
    }
  }

  const loadUtmSources = async () => {
    setLoading(prev => ({ ...prev, utm: true }))
    try {
      const tz = encodeURIComponent(getUserTimezone())
      const response = await api.get(`/api/analytics/utm?timeframe=${timeframe}&timezone=${tz}`)
      if (response.data.success) {
        setUtmSources(response.data.utmData?.slice(0, 5) || [])
      }
    } catch (error) {
      console.error('Failed to load UTM sources:', error)
    } finally {
      setLoading(prev => ({ ...prev, utm: false }))
    }
  }

  const loadQuestions = async () => {
    setLoading(prev => ({ ...prev, questions: true }))
    try {
      const tz = encodeURIComponent(getUserTimezone())
      const response = await api.get(`/api/analytics/common-questions?timeframe=${timeframe}&limit=5&timezone=${tz}`)
      if (response.data.success) {
        setQuestions(response.data.commonQuestions?.questions || [])
      }
    } catch (error) {
      console.error('Failed to load questions:', error)
    } finally {
      setLoading(prev => ({ ...prev, questions: false }))
    }
  }

  const loadAgents = async () => {
    setLoading(prev => ({ ...prev, agents: true }))
    try {
      const tz = encodeURIComponent(getUserTimezone())
      const response = await api.get(`/api/analytics/agents?timeframe=${timeframe}&timezone=${tz}`)
      if (response.data.success) {
        // The API LEFT JOINs agents → conversations, so it returns every team
        // member even ones who never handled a chat — those rows show up as
        // 0 chats / 0s response and falsely imply the human is slow. Only
        // surface agents who actually handled something this period; the
        // existing "No agent activity yet" empty state covers the rest.
        const agentData = (response.data.agentPerformance || [])
          .filter((a: any) => (a.totalConversations || 0) > 0)
          .slice(0, 5)
          .map((a: any) => ({
            id: a.id,
            name: a.name,
            chatsHandled: a.totalConversations,
            avgResponseTime: a.avgResponseTime,
            satisfaction: a.satisfactionScore
          }))
        setAgents(agentData)
      }
    } catch (error) {
      console.error('Failed to load agents:', error)
    } finally {
      setLoading(prev => ({ ...prev, agents: false }))
    }
  }

  const LoadingSkeleton = () => (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 animate-pulse">
          <div className="h-8 w-8 rounded bg-muted" />
          <div className="flex-1 space-y-1">
            <div className="h-4 w-3/4 bg-muted rounded" />
            <div className="h-3 w-1/2 bg-muted rounded" />
          </div>
          <div className="h-6 w-12 bg-muted rounded" />
        </div>
      ))}
    </div>
  )

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Analytics Overview</CardTitle>
            <CardDescription>Detailed breakdown by category</CardDescription>
          </div>
          <Link to="/analytics">
            <Button variant="ghost" size="sm" className="text-xs">
              View All
              <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full grid grid-cols-4 h-9">
            <TabsTrigger value="pages" className="text-xs px-2">
              <Globe className="h-3 w-3 mr-1 hidden sm:inline" />
              Pages
            </TabsTrigger>
            <TabsTrigger value="utm" className="text-xs px-2">
              <Link2 className="h-3 w-3 mr-1 hidden sm:inline" />
              Sources
            </TabsTrigger>
            <TabsTrigger value="questions" className="text-xs px-2">
              <MessageCircle className="h-3 w-3 mr-1 hidden sm:inline" />
              Questions
            </TabsTrigger>
            <TabsTrigger value="agents" className="text-xs px-2">
              <Trophy className="h-3 w-3 mr-1 hidden sm:inline" />
              Agents
            </TabsTrigger>
          </TabsList>

          {/* Top Pages Tab */}
          <TabsContent value="pages" className="mt-4">
            {loading.pages ? (
              <LoadingSkeleton />
            ) : topPages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground text-sm font-medium">No page data yet</p>
                <p className="text-xs text-muted-foreground mt-1">Shows which website pages generate the most chats once your widget is connected.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topPages.map((page, index) => (
                  <div key={page.path} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-muted-foreground w-6">
                      #{index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{page.title || page.path}</p>
                      <p className="text-xs text-muted-foreground truncate">{page.path}</p>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {page.chatCount} chats
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* UTM Sources Tab */}
          <TabsContent value="utm" className="mt-4">
            {loading.utm ? (
              <LoadingSkeleton />
            ) : utmSources.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground text-sm font-medium">No traffic source data yet</p>
                <p className="text-xs text-muted-foreground mt-1">Add UTM parameters to your links (e.g. ?utm_source=google) to track which campaigns drive chats.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {utmSources.map((utm, index) => (
                  <div key={`${utm.source}-${utm.medium}-${index}`} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-muted-foreground w-6">
                      #{index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{utm.source || 'Direct'}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {utm.medium} {utm.campaign && `/ ${utm.campaign}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{utm.count}</p>
                      {utm.conversions > 0 && (
                        <p className="text-xs text-green-600">{utm.conversions} conv</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Common Questions Tab */}
          <TabsContent value="questions" className="mt-4">
            {loading.questions ? (
              <LoadingSkeleton />
            ) : questions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground text-sm font-medium">No questions yet</p>
                <p className="text-xs text-muted-foreground mt-1">Common visitor questions will appear here as more chats come in.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {questions.map((q, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="text-sm font-medium text-muted-foreground w-6 pt-0.5">
                      #{index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm line-clamp-2">{q.question}</p>
                      {q.category && (
                        <Badge variant="outline" className="text-xs mt-1">
                          {q.category}
                        </Badge>
                      )}
                    </div>
                    <Badge variant="secondary" className="text-xs shrink-0">
                      {q.count}x
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Agent Leaderboard Tab */}
          <TabsContent value="agents" className="mt-4">
            {loading.agents ? (
              <LoadingSkeleton />
            ) : agents.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground text-sm font-medium">No agent activity yet</p>
                <p className="text-xs text-muted-foreground mt-1">Agent performance will show here once team members handle chats.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {agents.map((agent, index) => (
                  <div key={agent.id} className="flex items-center gap-3">
                    <span className={`text-sm font-medium w-6 ${
                      index === 0 ? 'text-yellow-500' :
                      index === 1 ? 'text-gray-400' :
                      index === 2 ? 'text-amber-600' :
                      'text-muted-foreground'
                    }`}>
                      #{index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{agent.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {agent.avgResponseTime.toFixed(1)}s avg response
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{agent.chatsHandled}</p>
                      <p className="text-xs text-muted-foreground">chats</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}
